import { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import { openSequenceMapPeek } from "../../../app/slices/GlobalSlice";
import VideocamIcon from "@mui/icons-material/Videocam";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ScheduleIcon from "@mui/icons-material/Schedule";
import StopIcon from "@mui/icons-material/Stop";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import { ActorMovementType } from "../../../models/ActorMovementType";
import { TakeSetupPlan, TakeSetupStep } from "../../../helpers/TakeSetupPlan";
import useTakeSetupNames from "./useTakeSetupNames";

type Names = ReturnType<typeof useTakeSetupNames>;

const MOVEMENT_LABELS: Record<ActorMovementType, string> = {
  [ActorMovementType.Instant]: "Instant",
  [ActorMovementType.Walk]: "Walk",
  [ActorMovementType.AutoWalk]: "Auto walk",
  [ActorMovementType.Explore]: "Explore",
};

const iconClassName = "flex-shrink-0 text-creator-text-soft";

function StepRow(props: {
  lead: ReactNode;
  verb: string;
  text: string;
  note?: string;
  sub?: string;
}) {
  return (
    <>
      <div
        className="flex min-h-[20px] items-center gap-[8px] text-creator-text"
        data-testid="take-setup-step"
      >
        <span className="flex w-[18px] flex-shrink-0 justify-center">{props.lead}</span>
        <span className="min-w-0 truncate" title={`${props.verb} ${props.text}`}>
          <span className="font-medium">{props.verb}</span> {props.text}
        </span>
        {props.note && (
          <span className="ml-auto flex-shrink-0 pl-[8px] text-creator-text-soft">
            {props.note}
          </span>
        )}
      </div>
      {props.sub && (
        <div className="flex min-h-[18px] items-center gap-[6px] pl-[26px] text-creator-text-soft">
          <span aria-hidden="true">↳</span>
          {props.sub}
        </div>
      )}
    </>
  );
}

type PositionOf = (step: TakeSetupStep) => string | undefined;

/** A step that has a place on the map opens the map there. */
function FocusableStep(props: { step: TakeSetupStep; names: Names; positionOf: PositionOf }) {
  const dispatch = useDispatch();
  const point = props.names.positionPoint(props.positionOf(props.step));
  const row = <Step step={props.step} names={props.names} />;
  if (!point) return row;

  return (
    <div
      {...pressProps(() => dispatch(openSequenceMapPeek(point)))}
      title="Show on the map"
      className={classNames("-mx-[6px] flex flex-col rounded-[4px] px-[6px]", clickableClassName)}
    >
      {row}
    </div>
  );
}

function Step({ step, names }: { step: TakeSetupStep; names: Names }) {
  const actor = (actorId: string) => (
    <ActorAvatar actor={names.findActor(actorId)} size="22" theme="creator" />
  );
  const prop = <Inventory2OutlinedIcon className={iconClassName} sx={{ fontSize: 16 }} />;
  const clip = <AttachFileIcon className={iconClassName} sx={{ fontSize: 16 }} />;
  const { actorName, propName, positionName, targetName } = names;

  switch (step.kind) {
    case "despawnActor":
      return <StepRow lead={actor(step.actorId)} verb="Despawn Actor" text={actorName(step.actorId)} />;
    case "destroyProp":
      return <StepRow lead={prop} verb="Destroy Prop" text={propName(step.propId)} />;
    case "detachProp":
      return (
        <StepRow
          lead={clip}
          verb="Detach Prop"
          text={`${propName(step.propId)} ← ${actorName(step.actorId)}`}
          note="removed from the hand"
        />
      );
    case "detachActor":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Detach Actor"
          text={`${actorName(step.actorId)} ← ${propName(step.propId)}`}
        />
      );
    case "camera":
      return (
        <StepRow
          lead={<VideocamIcon className={iconClassName} sx={{ fontSize: 16 }} />}
          verb="Camera"
          text={positionName(step.cameraId)}
          note={step.cut ? "cut" : "move"}
        />
      );
    case "inspectProp":
      return (
        <StepRow
          lead={<VideocamIcon className={iconClassName} sx={{ fontSize: 16 }} />}
          verb="Inspect Prop"
          text={`${propName(step.propId)} · ${step.tag}`}
          note={step.cut ? "cut" : "move"}
        />
      );
    case "spawnActor":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Spawn Actor"
          text={`${actorName(step.actorId)} @ ${positionName(step.positionId)}`}
        />
      );
    case "moveActor":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Move Actor"
          text={`${actorName(step.actorId)} → ${positionName(step.positionId)}`}
          note={MOVEMENT_LABELS[step.movement]}
          sub={
            step.releasedFromPropId
              ? `first: Detach ${actorName(step.actorId)} ← ${propName(step.releasedFromPropId)}, back to idle`
              : undefined
          }
        />
      );
    case "setOutfit":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Set Outfit"
          text={`${actorName(step.actorId)} → ${step.outfit}`}
        />
      );
    case "resetCharacterParts":
      return <StepRow lead={actor(step.actorId)} verb="Reset Parts" text={actorName(step.actorId)} />;
    case "animate":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Animate Actor"
          text={`${actorName(step.actorId)} · ${step.stateName}`}
        />
      );
    case "lookAt":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Look At"
          text={`${actorName(step.actorId)} → ${targetName(step.targetId)}`}
        />
      );
    case "stopLooking":
      return <StepRow lead={actor(step.actorId)} verb="Stop Looking" text={actorName(step.actorId)} />;
    case "spawnProp":
      return (
        <StepRow
          lead={prop}
          verb="Spawn Prop"
          text={`${propName(step.propId)} @ ${positionName(step.positionId)}`}
        />
      );
    case "moveProp":
      return (
        <StepRow
          lead={prop}
          verb="Move Prop"
          text={`${propName(step.propId)} → ${positionName(step.positionId)}`}
        />
      );
    case "attachProp":
      return (
        <StepRow
          lead={clip}
          verb="Attach Prop"
          text={`${propName(step.propId)} → ${actorName(step.actorId)}`}
          note={`${step.source === "pickedUp" ? "picked up" : "in the hand"} · tag ${step.tag}`}
        />
      );
    case "attachActor":
      return (
        <StepRow
          lead={actor(step.actorId)}
          verb="Attach Actor"
          text={`${actorName(step.actorId)} → ${propName(step.propId)}`}
          note={`tag ${step.tag}`}
        />
      );
    case "startDelay":
      return (
        <StepRow
          lead={<ScheduleIcon className={iconClassName} sx={{ fontSize: 16 }} />}
          verb="Start delay"
          text={`${step.seconds} s`}
          note="new location"
        />
      );
  }
}

export const cellClassName =
  "flex flex-col gap-[6px] rounded-[4px] border border-solid border-creator-border bg-creator-background px-[10px] py-[8px]";
export const thinCellClassName =
  "flex items-center gap-[8px] rounded-[4px] border border-solid border-creator-border bg-creator-background px-[10px] py-[5px] text-creator-text-soft";

export function Then() {
  return (
    <div className="flex items-center gap-[8px] text-creator-text-soft before:h-px before:flex-1 before:bg-creator-border after:h-px after:flex-1 after:bg-creator-border">
      then
    </div>
  );
}

function Cell(props: {
  title: string;
  steps: TakeSetupStep[];
  names: Names;
  name: string;
  positionOf: PositionOf;
}) {
  if (props.steps.length === 0) return null;
  return (
    <div className={cellClassName} data-testid={`take-setup-${props.name}`}>
      <div className="font-medium text-creator-text">{props.title}</div>
      {props.steps.map((step, index) => (
        <FocusableStep
          key={index}
          step={step}
          names={props.names}
          positionOf={props.positionOf}
        />
      ))}
    </div>
  );
}

export function Fade({ label, seconds }: { label: string; seconds: number }) {
  return (
    <div className={thinCellClassName}>
      <span>{label}</span>
      <span className="ml-auto tabular-nums">{seconds} s</span>
    </div>
  );
}

export const FADE_SECONDS = 0.5;

/** The previous take's On Take End, which plays at the same time as this take's setup. */
export type PreviousTakeExit = {
  takeName: string;
  nodeCount: number;
  open: () => void;
};

// Width of the "At the same time" column, and the gap before it.
export const ALONGSIDE_WIDTH = 184;
export const ALONGSIDE_GAP = 10;
/** From the right of the setup cell's content out to the node's edge: cell padding and border, then node padding. */
export const HANDLE_OUTSET = 27;
/** Content width of the take trigger nodes (400 px with padding), wider with a column beside the setup. Both nodes of a take use it, so they always match. */
export const getTakeFlowWidth = (hasAlongside: boolean) => (hasAlongside ? 528 : 368);

export const clickableClassName =
  "cursor-pointer outline-none hover:bg-creator-primary-washed focus-visible:ring-2 focus-visible:ring-creator-primary";

/** Runs `action` on a click or Enter/Space, without selecting the node. */
export function pressProps(action: () => void) {
  return {
    role: "button",
    tabIndex: 0,
    onClick: (event: MouseEvent) => {
      event.stopPropagation();
      action();
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      event.stopPropagation();
      action();
    },
  };
}

/** A column beside the setup: what is in it starts at the same moment and runs next to it. */
export function Alongside(props: { children: ReactNode; testId: string }) {
  return (
    <div
      className="box-border flex flex-shrink-0 flex-col gap-[6px] border-0 border-l border-dashed border-creator-border pl-[10px]"
      style={{ width: ALONGSIDE_WIDTH }}
      data-testid={props.testId}
    >
      <div className="text-creator-text-soft">At the same time</div>
      {props.children}
    </div>
  );
}

function PreviousExit({ exit }: { exit: PreviousTakeExit }) {
  return (
    <Alongside testId="take-setup-alongside">
      <div
        {...pressProps(exit.open)}
        title={`Open ${exit.takeName}`}
        className={classNames(
          "flex items-center gap-[6px] rounded-[4px] border border-dashed border-creator-border bg-creator-background px-[8px] py-[6px]",
          clickableClassName,
        )}
        data-testid="take-setup-previous-exit"
      >
        <StopIcon className="flex-shrink-0 text-creator-error" sx={{ fontSize: 14 }} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="break-words font-medium text-creator-text">{exit.takeName} · On Take End</span>
          <span className="text-creator-text-soft">
            {exit.nodeCount} {exit.nodeCount === 1 ? "node" : "nodes"}
          </span>
        </span>
        <ChevronRightIcon className="flex-shrink-0 text-creator-text-soft" sx={{ fontSize: 16 }} />
      </div>
    </Alongside>
  );
}

/**
 * The take start in the order the compiled take runs it, with the trigger's
 * own nodes starting once the setup is done. `handle` is the trigger's output.
 */
export default function TakeSetupFlow(props: {
  plan: TakeSetupPlan;
  triggerLabel: string;
  handle: ReactNode;
  positionOf: PositionOf;
  previousExit?: PreviousTakeExit;
}) {
  const names = useTakeSetupNames();
  const stepProps = { names, positionOf: props.positionOf };
  const { plan, previousExit } = props;
  const stages = [
    { name: "clear", title: "Clear", steps: plan.clear },
    { name: "spawns-and-moves", title: "Spawns and moves", steps: plan.spawnsAndMoves },
    { name: "attachments", title: "Attachments", steps: plan.attachments },
    { name: "look-ats", title: "Look-ats", steps: plan.lookAts },
  ].filter((stage) => stage.steps.length > 0);

  const setup = (
    <>
      {stages.length === 0 && (
        <div className="text-creator-text-soft">Nothing changes from the previous take</div>
      )}
      {stages.map((stage, index) => (
        <div key={stage.name} className="flex flex-col gap-[4px]" data-testid={`take-setup-${stage.name}`}>
          {index > 0 && <Then />}
          <div className="font-medium text-creator-text-soft">{stage.title}</div>
          {stage.steps.map((step, stepIndex) => (
            <FocusableStep key={stepIndex} step={step} {...stepProps} />
          ))}
        </div>
      ))}
      <Then />
      <div className="relative flex flex-col gap-[2px] rounded-[4px] bg-creator-primary-washed px-[10px] py-[8px]">
        <span className="font-medium text-creator-text">{props.triggerLabel}</span>
        <span className="text-creator-text-soft">
          {plan.transition
            ? "After the setup is done, while the screen is still black"
            : "After the setup is done, before the camera moves"}
        </span>
        {/* Out to the node's edge, past the column beside the setup when there is one. */}
        <div
          className="absolute top-1/2 visible"
          style={{
            right: -(HANDLE_OUTSET + (previousExit ? ALONGSIDE_WIDTH + ALONGSIDE_GAP : 0)),
          }}
        >
          {props.handle}
        </div>
      </div>
    </>
  );

  return (
    <div
      className="flex flex-col gap-[3px] text-[12px] leading-[16px]"
      style={{ width: getTakeFlowWidth(Boolean(previousExit)) }}
      data-testid="take-setup-flow"
    >
      <Cell
        name="player-walk"
        title={plan.transition ? "Before the transition, in view" : "Alongside the setup"}
        steps={plan.playerWalk}
        {...stepProps}
      />
      {plan.transition && <Fade label="Fade in" seconds={FADE_SECONDS} />}
      <div className={classNames(cellClassName, "relative")} data-testid="take-setup-setup">
        {plan.transition ? (
          <div className="-mx-[10px] -mt-[8px] flex items-center rounded-t-[4px] bg-black px-[10px] py-[5px] font-medium text-white">
            Black screen
            <span className="ml-auto font-normal tabular-nums opacity-70">
              {plan.transition.duration} s
            </span>
          </div>
        ) : (
          <div className="font-medium text-creator-text">Setup, in view</div>
        )}
        {previousExit ? (
          <div className="flex" style={{ gap: ALONGSIDE_GAP }}>
            <div className="flex min-w-0 flex-1 flex-col gap-[6px]">{setup}</div>
            <PreviousExit exit={previousExit} />
          </div>
        ) : (
          setup
        )}
      </div>
      <Cell name="camera" title="Camera moves" steps={plan.cameraAfterSetup} {...stepProps} />
      {plan.transition && <Fade label="Fade out" seconds={FADE_SECONDS} />}
      <Cell name="walks" title="Walks until arrived" steps={plan.walks} {...stepProps} />
      <Cell
        name="before-interactions"
        title="Before the interactions"
        steps={plan.beforeInteractions}
        {...stepProps}
      />
    </div>
  );
}
